
import {
  Add as AddIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Flag as FlagIcon,
  BarChart as BarChartIcon,
  Security as SecurityIcon,
  Settings as ConfigIcon,
  Public as PopularIcon,
  Analytics as AnalyticsIcon,
  FilterList as BrowseIcon,
  Category as CategoryIcon,
  SvgIconComponent
} from '@mui/icons-material';
import { UserRole } from '../types/user';

// Define types for navigation

export interface NavigationBadge {
  show: boolean;
  value?: number | string;
  max?: number;
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

export interface NavigationItem {
  id?: string;
  label: string;
  path: string;
  icon: SvgIconComponent;
  requireAuth?: boolean;
  adminOnly?: boolean;
  exact?: boolean;
  roles?: UserRole[];
  badge?: NavigationBadge;
  disabled?: boolean;
  external?: boolean;
  children?: NavigationItem[];
}

export const mainNavigationItems: NavigationItem[] = [
  { 
    id: 'explore',
    label: 'Explore', 
    path: '/explore', 
    icon: PopularIcon
  },
  { 
    id: 'browse',
    label: 'Browse', 
    path: '/browse', 
    icon: BrowseIcon
  },
  { 
    id: 'categories',
    label: 'Categories', 
    path: '/categories', 
    icon: CategoryIcon
  },
  { 
    id: 'create-post',
    label: 'Create', 
    path: '/create-post', 
    icon: AddIcon,
    requireAuth: true
  }
];

export const userNavigationItems: NavigationItem[] = [
  { 
    id: 'profile',
    label: 'Profile', 
    path: '/profile', 
    icon: PersonIcon,
    requireAuth: true
  },
  { 
    id: 'analytics',
    label: 'Analytics', 
    path: '/analytics', 
    icon: AnalyticsIcon,
    requireAuth: true
  },
  { 
    id: 'settings',
    label: 'Settings', 
    path: '/settings', 
    icon: SettingsIcon,
    requireAuth: true
  },
  { 
    id: 'admin',
    label: 'Admin Dashboard', 
    path: '/admin', 
    icon: AdminIcon,
    requireAuth: true,
    adminOnly: true,
    roles: ['admin', 'moderator']
  }
];

// Admin dashboard navigation items
export const adminNavigationItems: NavigationItem[] = [
  {
    id: 'admin-dashboard',
    label: 'Dashboard',
    path: '/admin',
    icon: DashboardIcon,
    exact: true
  },
  {
    id: 'user-management',
    label: 'User Management',
    path: '/admin/users',
    icon: PeopleIcon
  },
  {
    id: 'content-moderation',
    label: 'Content Moderation',
    path: '/admin/moderation',
    icon: FlagIcon,
    children: [
      {
        id: 'reported-content',
        label: 'Reports',
        path: '/admin/moderation/reports',
        icon: FlagIcon
      }
    ]
  },
  {
    id: 'system',
    label: 'System',
    path: '/admin/system',
    icon: SecurityIcon,
    roles: ['admin'],
    children: [
      {
        id: 'system-metrics',
        label: 'Metrics',
        path: '/admin/system/metrics',
        icon: BarChartIcon
      },
      {
        id: 'system-settings',
        label: 'Settings',
        path: '/admin/system/settings',
        icon: ConfigIcon
      },
      {
        id: 'system-security',
        label: 'Security',
        path: '/admin/system/security',
        icon: SecurityIcon
      }
    ]
  }
];

// Helper function to check if a route is active
export const isActiveRoute = (currentPath: string, itemPath: string, exact = false): boolean => {
  if (exact) {
    return currentPath === itemPath;
  }
  if (itemPath === '/' && currentPath === '/') return true;
  if (itemPath !== '/' && currentPath.startsWith(itemPath)) return true;
  return false;
};

// Helper to check if the user has required role for a navigation item
export const hasRequiredRole = (item: NavigationItem, userRoles?: UserRole[]): boolean => {
  if (!item.roles || item.roles.length === 0) return true;
  if (!userRoles || userRoles.length === 0) return false;
  
  return item.roles.some(role => userRoles.includes(role));
};

// Filter navigation items based on auth status and roles
export const filterNavigationItems = (
  items: NavigationItem[], 
  isAuthenticated: boolean, 
  userRoles?: UserRole[]
): NavigationItem[] => {
  return items.filter(item => {
    // Filter out items that require auth when user is not authenticated
    if (item.requireAuth && !isAuthenticated) return false;
    
    // Filter out admin-only items for non-admin users
    if (item.adminOnly && (!userRoles || !userRoles.includes('admin'))) return false;
    
    // Check for role-based access
    if (item.roles && item.roles.length > 0) {
      if (!userRoles || !hasRequiredRole(item, userRoles)) return false;
    }
    
    // If item has children, filter those too
    if (item.children && item.children.length > 0) {
      const filteredChildren = filterNavigationItems(item.children, isAuthenticated, userRoles);
      item = { ...item, children: filteredChildren };
      
      // If all children are filtered out, don't show parent either
      if (filteredChildren.length === 0 && item.path.includes('/admin')) return false;
    }
    
    return true;
  });
};

// Update badge value for a navigation item
export const updateNavigationBadge = (
  items: NavigationItem[],
  itemId: string,
  value: number | string,
  show: boolean = true
): NavigationItem[] => {
  return items.map(item => {
    if (item.id === itemId) {
      return {
        ...item,
        badge: {
          ...item.badge,
          value,
          show
        }
      };
    }
    
    if (item.children) {
      return {
        ...item,
        children: updateNavigationBadge(item.children, itemId, value, show)
      };
    }
    
    return item;
  });
};

